// TEXT_RECORDING — Section 10. Recording starts immediately on screen appear.

import { useEffect, useRef, useState } from 'react';
import { AudioRecorder } from '../audio/recorder';
import RecordingIndicator from '../components/RecordingIndicator';
import StopButton from '../components/StopButton';
import ConfirmationModal from '../components/ConfirmationModal';
import { useSessionStore } from '../store/sessionStore';
import { useParticipantStore } from '../store/participantStore';
import { getPassage, getManifest } from '../utils/content';
import { collectDeviceInfo } from '../utils/manifest';

export default function TextRecordingScreen({ onContinue, onAbandon }) {
  const contentIndex = useSessionStore((s) => s.contentIndex);
  const setTextTrial = useSessionStore((s) => s.setTextTrial);
  const setDeviceInfo = useSessionStore((s) => s.setDeviceInfo);
  const abandon = useSessionStore((s) => s.abandon);
  const participantPatch = useParticipantStore((s) => s.patch);

  const recorderRef = useRef(null);
  const cueOnsetRef = useRef(null);
  const [passage, setPassage] = useState(null);
  const [error, setError] = useState(null);
  const [confirmingDone, setConfirmingDone] = useState(false);
  const [confirmingStop, setConfirmingStop] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const manifest = await getManifest();
      const key = manifest?.texts?.[contentIndex % (manifest.texts.length || 1)]?.key ?? `passage_${String(contentIndex + 1).padStart(2, '0')}`;
      const p = await getPassage(key);
      if (cancelled) return;
      if (!p) {
        setError('This passage is not available offline. Reconnect and try again.');
        return;
      }
      setPassage(p);
      cueOnsetRef.current = new Date().toISOString();

      try {
        const recorder = new AudioRecorder();
        await recorder.start();
        recorderRef.current = recorder;
        setStartedAt(recorder.startedAt);
        setDeviceInfo(
          collectDeviceInfo(recorder.constraintsRequested, recorder.constraintsActual, recorder.mimeType)
        );
      } catch (err) {
        setError('Microphone access is required to continue. Please allow microphone access and reopen the app.');
      }
    })();
    return () => {
      cancelled = true;
      recorderRef.current?.abort();
    };
  }, [contentIndex]);

  async function handleDone() {
    const recorder = recorderRef.current;
    const blob = await recorder.stop();
    setTextTrial({
      trial_id: crypto.randomUUID(),
      content_key: passage.key,
      passage_text: passage.text,
      cue_onset_ts_utc: cueOnsetRef.current,
      recording_start_ts_utc: recorder.startedAt,
      recording_end_ts_utc: recorder.endedAt,
      duration_ms: new Date(recorder.endedAt) - new Date(recorder.startedAt),
      blob,
    });
    onContinue();
  }

  async function handleStop() {
    recorderRef.current?.abort();
    abandon('text_recording');
    await participantPatch({ active_in_session: false, current_session_id: null });
    onAbandon();
  }

  if (error) {
    return (
      <div className="screen justify-center text-center">
        <p className="text-stop mb-6">{error}</p>
        <StopButton label="Back" onClick={() => setConfirmingStop(true)} />
        <ConfirmationModal
          open={confirmingStop}
          title="Stop this session?"
          message="Nothing will be saved from this session."
          confirmLabel="Yes, stop"
          cancelLabel="No, keep going"
          danger
          onCancel={() => setConfirmingStop(false)}
          onConfirm={handleStop}
        />
      </div>
    );
  }

  return (
    <div className="screen">
      <h1 className="font-display text-2xl mb-4">Read this aloud</h1>

      <div className="mb-4">{startedAt && <RecordingIndicator startedAt={startedAt} />}</div>

      <div className="card flex-1 overflow-y-auto mb-6">
        {passage ? (
          <p className="text-xl leading-relaxed">{passage.text}</p>
        ) : (
          <p className="text-dim">Loading passage…</p>
        )}
      </div>

      <button type="button" disabled={!passage} onClick={() => setConfirmingDone(true)} className="btn-primary">
        Finish Reading
      </button>
      <StopButton onClick={() => setConfirmingStop(true)} />

      <ConfirmationModal
        open={confirmingDone}
        title="Done reading?"
        message="This will move to the image task."
        confirmLabel="Yes"
        cancelLabel="No, keep going"
        onCancel={() => setConfirmingDone(false)}
        onConfirm={() => {
          setConfirmingDone(false);
          handleDone();
        }}
      />
      <ConfirmationModal
        open={confirmingStop}
        title="Stop this session?"
        message="Nothing will be saved from this session."
        confirmLabel="Yes, stop"
        cancelLabel="No, keep going"
        danger
        onCancel={() => setConfirmingStop(false)}
        onConfirm={handleStop}
      />
    </div>
  );
}
